import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Label, TextField } from '@printtat/ui';

interface PrintAreas {
  surfaces: string[];
  dimensions: Record<string, { width: number; height: number }>;
  canvasOffset: Record<string, { x: number; y: number }>;
  canvasWidthRatio: Record<string, number>;
  safeAreas: Record<string, { margin: number }>;
  mockups: Record<string, string>;
  surfaceLabels: Record<string, string>;
}

interface PrintAreasEditorProps {
  value: PrintAreas;
  onChange: (value: PrintAreas) => void;
}

const DEFAULT_SURFACE_CONFIG = {
  dimensions: { width: 440, height: 600 },
  canvasOffset: { x: 0, y: 0 },
  canvasWidthRatio: 0.5,
  safeAreas: { margin: 50 },
};

export const PrintAreasEditor = ({ value, onChange }: PrintAreasEditorProps) => {
  const [expandedSurfaces, setExpandedSurfaces] = useState<Set<string>>(new Set(value.surfaces));
  const [newSurfaceKey, setNewSurfaceKey] = useState('');

  const toggleExpanded = (surface: string) => {
    setExpandedSurfaces((prev) => {
      const next = new Set(prev);
      if (next.has(surface)) next.delete(surface);
      else next.add(surface);
      return next;
    });
  };

  const addSurface = () => {
    const key = newSurfaceKey.trim();
    if (!key || value.surfaces.includes(key)) return;

    onChange({
      ...value,
      surfaces: [...value.surfaces, key],
      dimensions: { ...value.dimensions, [key]: { ...DEFAULT_SURFACE_CONFIG.dimensions } },
      canvasOffset: { ...value.canvasOffset, [key]: { ...DEFAULT_SURFACE_CONFIG.canvasOffset } },
      canvasWidthRatio: { ...value.canvasWidthRatio, [key]: DEFAULT_SURFACE_CONFIG.canvasWidthRatio },
      safeAreas: { ...value.safeAreas, [key]: { ...DEFAULT_SURFACE_CONFIG.safeAreas } },
      mockups: { ...value.mockups, [key]: '' },
      surfaceLabels: { ...value.surfaceLabels, [key]: key.charAt(0).toUpperCase() + key.slice(1) },
    });
    setNewSurfaceKey('');
    setExpandedSurfaces((prev) => new Set([...prev, key]));
  };

  const removeSurface = (surface: string) => {
    const surfaces = value.surfaces.filter((s) => s !== surface);
    const remove = (obj: Record<string, unknown>) => {
      const next = { ...obj };
      delete next[surface];
      return next;
    };
    onChange({
      surfaces,
      dimensions: remove(value.dimensions) as PrintAreas['dimensions'],
      canvasOffset: remove(value.canvasOffset) as PrintAreas['canvasOffset'],
      canvasWidthRatio: remove(value.canvasWidthRatio) as PrintAreas['canvasWidthRatio'],
      safeAreas: remove(value.safeAreas) as PrintAreas['safeAreas'],
      mockups: remove(value.mockups) as PrintAreas['mockups'],
      surfaceLabels: remove(value.surfaceLabels) as PrintAreas['surfaceLabels'],
    });
  };

  const updateDimensions = (surface: string, field: 'width' | 'height', val: string) => {
    const num = parseInt(val, 10);
    if (isNaN(num)) return;
    onChange({
      ...value,
      dimensions: {
        ...value.dimensions,
        [surface]: { ...value.dimensions[surface], [field]: num },
      },
    });
  };

  const updateOffset = (surface: string, field: 'x' | 'y', val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    onChange({
      ...value,
      canvasOffset: {
        ...value.canvasOffset,
        [surface]: { ...value.canvasOffset[surface], [field]: num },
      },
    });
  };

  const updateWidthRatio = (surface: string, val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    onChange({
      ...value,
      canvasWidthRatio: { ...value.canvasWidthRatio, [surface]: num },
    });
  };

  const updateSafeArea = (surface: string, val: string) => {
    const num = parseInt(val, 10);
    if (isNaN(num)) return;
    onChange({
      ...value,
      safeAreas: { ...value.safeAreas, [surface]: { margin: num } },
    });
  };

  const updateLabel = (surface: string, label: string) => {
    onChange({
      ...value,
      surfaceLabels: { ...value.surfaceLabels, [surface]: label },
    });
  };

  const updateMockup = (surface: string, path: string) => {
    onChange({
      ...value,
      mockups: { ...value.mockups, [surface]: path },
    });
  };

  return (
    <div className="space-y-3">
      {/* Surface cards */}
      {value.surfaces.map((surface) => {
        const isExpanded = expandedSurfaces.has(surface);
        const dims = value.dimensions[surface];
        const offset = value.canvasOffset[surface];
        const ratio = value.canvasWidthRatio[surface];
        const safeArea = value.safeAreas[surface];
        const label = value.surfaceLabels[surface] ?? surface;

        return (
          <div key={surface} className="border rounded-xl bg-white">
            {/* Header */}
            <button
              onClick={() => toggleExpanded(surface)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                <span className="text-sm font-medium text-gray-900">{label}</span>
                <span className="text-xs text-gray-400 font-mono">{surface}</span>
              </div>
              <span className="text-xs text-gray-400">
                {dims?.width}x{dims?.height}
              </span>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t pt-3 space-y-3">
                {/* Label */}
                <div>
                  <Label className="block text-xs text-gray-500 mb-1">Display Label</Label>
                  <TextField
                    value={label}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateLabel(surface, e.target.value)}
                    className="w-48"
                  />
                </div>

                {/* Dimensions + Offset row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Width (px)</Label>
                    <TextField
                      type="number"
                      min="1"
                      value={String(dims?.width ?? 0)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDimensions(surface, 'width', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Height (px)</Label>
                    <TextField
                      type="number"
                      min="1"
                      value={String(dims?.height ?? 0)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateDimensions(surface, 'height', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Offset X</Label>
                    <TextField
                      type="number"
                      value={String(offset?.x ?? 0)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOffset(surface, 'x', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Offset Y</Label>
                    <TextField
                      type="number"
                      value={String(offset?.y ?? 0)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateOffset(surface, 'y', e.target.value)}
                    />
                  </div>
                </div>

                {/* Width Ratio + Safe Area row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Width Ratio (0–1)</Label>
                    <TextField
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={String(ratio ?? 0.5)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateWidthRatio(surface, e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="block text-xs text-gray-500 mb-1">Safe Area Margin (px)</Label>
                    <TextField
                      type="number"
                      min="0"
                      value={String(safeArea?.margin ?? 0)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSafeArea(surface, e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="block text-xs text-gray-500 mb-1">Mockup Path</Label>
                    <TextField
                      value={value.mockups[surface] ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateMockup(surface, e.target.value)}
                      placeholder="/products/slug/illustrations/surface.svg"
                    />
                  </div>
                </div>

                {/* Remove button */}
                <div className="pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    color="red"
                    onClick={() => removeSurface(surface)}
                  >
                    <Trash2 size={12} className="mr-1" />
                    Remove Surface
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add surface */}
      <div className="flex items-center gap-2">
        <TextField
          placeholder="Surface key (e.g. front, back, leftSleeve)"
          value={newSurfaceKey}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewSurfaceKey(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') addSurface(); }}
          className="flex-1 max-w-xs"
        />
        <Button size="sm" variant="outline" onClick={addSurface} disabled={!newSurfaceKey.trim()}>
          <Plus size={14} className="mr-1" />
          Add Surface
        </Button>
      </div>
    </div>
  );
};
